import { useRef, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Whatsapp from '../../assets/icons/trvto_whatsapp.svg';
import './WhatsAppFloatButton.css';

const WhatsAppFloatButton = () => {
    const { pathname } = useLocation();
    const buttonRef = useRef(null);
    const offset = useRef({ x: 0, y: 0 });
    const moved = useRef(false);
    const startPoint = useRef({ x: 0, y: 0 });

    const [position, setPosition] = useState(() => ({
        x: window.innerWidth <= 768 ? 20 : window.innerWidth - 76,
        y: window.innerHeight - 140
    }));

    const [dragging, setDragging] = useState(false);
    const [showMessage, setShowMessage] = useState(false);
    const [tooltipDirection, setTooltipDirection] = useState('left');
    const [tempOrg, setTempOrg] = useState({});

    // Tooltip side
    useEffect(() => {
        const mid = window.innerWidth / 2;
        setTooltipDirection(position.x < mid ? 'right' : 'left');
    }, [position]);

    // Auto tooltip
    useEffect(() => {
        const interval = setInterval(() => {
            setShowMessage(true);
            const timeout = setTimeout(() => setShowMessage(false), 5000);
            return () => clearTimeout(timeout);
        }, 10000);

        return () => clearInterval(interval);
    }, []);

    // Org channel
    useEffect(() => {
        const channel = new BroadcastChannel('org_channel');

        channel.onmessage = (event) => {
            if (event.data.type === 'UPDATE_ORG') {
                setTempOrg(event.data.payload);
            } else if (event.data.type === 'REMOVE_ORG') {
                setTempOrg({});
            }
        };

        return () => channel.close();
    }, []);

    // Drag logic
    useEffect(() => {
        const handleMove = (e) => {
            if (!dragging) return;

            e.preventDefault();

            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            const clientY = e.touches ? e.touches[0].clientY : e.clientY;

            // detect real drag
            if (Math.abs(clientX - startPoint.current.x) > 5 || Math.abs(clientY - startPoint.current.y) > 5) {
                moved.current = true;
            }

            if (!moved.current) return;

            let newX = clientX - offset.current.x;
            let newY = clientY - offset.current.y;

            const boundedX = Math.min(window.innerWidth - 56, Math.max(0, newX));
            const boundedY = Math.min(window.innerHeight - 56, Math.max(0, newY));

            setPosition({ x: boundedX, y: boundedY });
        };

        const stopDragging = () => {
            setDragging(false);

            if (!moved.current) return;

            const middle = window.innerWidth / 2;

            setPosition((prev) => ({
                x: prev.x < middle ? 20 : window.innerWidth - 76,
                y: prev.y
            }));
        };

        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', stopDragging);
        window.addEventListener('touchmove', handleMove, { passive: false });
        window.addEventListener('touchend', stopDragging);
        window.addEventListener('touchcancel', stopDragging);

        return () => {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', stopDragging);
            window.removeEventListener('touchmove', handleMove);
            window.removeEventListener('touchend', stopDragging);
            window.removeEventListener('touchcancel', stopDragging);
        };
    }, [dragging]);

    const startDragging = (e) => {
        e.preventDefault();
        e.stopPropagation();

        moved.current = false;

        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;

        startPoint.current = { x: clientX, y: clientY };

        const rect = buttonRef.current.getBoundingClientRect();

        offset.current = {
            x: clientX - rect.left,
            y: clientY - rect.top
        };

        setDragging(true);
    };

    const handleClick = (e) => {
        if (moved.current) {
            e.preventDefault();
        }
    };

    // Customer help button only — hide it on the support dashboard
    if (pathname.startsWith('/support')) return null;

    return (
        <div
            ref={buttonRef}
            className='whatsapp-float-button'
            onMouseDown={startDragging}
            onTouchStart={startDragging}
            style={{
                left: position.x,
                top: position.y
            }}
        >
            {showMessage && <div className={`whatsapp-tooltip ${tooltipDirection}`}>For any doubts, contact</div>}

            <a
                href={
                    tempOrg?._id
                        ? `https://wa.me/91${tempOrg?.contactPerson?.phoneNumber}`
                        : `https://wa.me/918088788874`
                }
                target='_blank'
                rel='noopener noreferrer'
                onClick={handleClick}
            >
                <img src={Whatsapp} alt='WhatsApp' />
            </a>
        </div>
    );
};

export default WhatsAppFloatButton;
